import { Stack, ThemeProvider } from "@mui/material"
import { useState } from "react"

import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { DtDateRange } from "../../../../../../shared/components/table/StandardDateRangeFilter"
import { useInitialDateFilters } from "../../../../../../shared/components/table/useInitialDateFilters"
import { selectServiceCenterId } from "../../../../../login/loginSelectors"
import { AccountingCustomer } from "../../../../../merchant/accounting/accounting/accountingApi"
import { useAppSelector } from "../../../../../store/storeHooks"
import { ResourceType } from "../../../../admin/organization/resources/resourcesTypes"
import { useExportUsagePerTenant } from "../../hooks/useExportUsagePerTenant"
import { fetchUsagePerTenantSummary, ServiceCenterStatisticsQuery } from "../../statisticsApi"

import { ResourceUsageSections } from "./ResourceUsageSections"
import { TenantUsageFilters } from "./TenantUsageFilters"
import { TenantUsageHeader } from "./TenantUsageHeader"
import { SectionsSkeleton, SummarySkeleton } from "./TenantUsageSkeleton"
import { TenantUsageSummary } from "./TenantUsageSummary"
import { tenantUsageTheme } from "./tenantUsageTheme"
import { toSummaryViewModel } from "./tenantUsageViewModel"
import { useUsagePerTenantResources } from "./useUsagePerTenantResources"

export function TenantUsage() {
    const serviceCenterId = useAppSelector(selectServiceCenterId)
    const { startDate, endDate } = useInitialDateFilters()

    const [dateRange, setDateRange] = useState<DtDateRange>({ startDate, endDate })
    const [resourceType, setResourceType] = useState<ResourceType | undefined>(undefined)
    const [tenant, setTenant] = useState<AccountingCustomer | null>(null)

    const baseDeps = {
        serviceCenterId: serviceCenterId ?? 0,
        startDate: dateRange.startDate.toISODate(),
        endDate: dateRange.endDate.toISODate(),
        companyId: tenant?.id,
    }

    const summaryResult = useApiQuery(fetchUsagePerTenantSummary, {
        queryName: ServiceCenterStatisticsQuery.SERVICE_CENTER_STATISTICS_USAGE_PER_TENANT_SUMMARY,
        dependencies: { ...baseDeps, resourceType },
        select: toSummaryViewModel,
    })

    const resources = useUsagePerTenantResources(baseDeps, resourceType)

    const exportUsage = useExportUsagePerTenant({
        data: resources.rates,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
    })

    return (
        <ThemeProvider theme={tenantUsageTheme}>
            <Stack gap="1.5rem" paddingTop="1rem">
                <TenantUsageHeader />

                <TenantUsageFilters
                    dateRange={dateRange}
                    onDateRangeChange={setDateRange}
                    resourceType={resourceType}
                    onResourceTypeChange={setResourceType}
                    tenant={tenant}
                    onTenantChange={setTenant}
                    onExport={exportUsage}
                />

                {summaryResult.isInitialLoading && <SummarySkeleton />}
                {!summaryResult.isInitialLoading && summaryResult.data && (
                    <TenantUsageSummary summary={summaryResult.data} />
                )}

                {resources.isInitialLoading ? (
                    <SectionsSkeleton />
                ) : (
                    <ResourceUsageSections groups={resources.groups} />
                )}
            </Stack>
        </ThemeProvider>
    )
}
